import { pipe } from "effect/Function";
import * as Arr from "effect/Array";
import * as O from "effect/Order";
import type {
  MessageId,
  OrchestrationEvent,
  OrchestrationLatestTurn,
  OrchestrationMessage,
  OrchestrationSession,
  OrchestrationThread,
  OrchestrationThreadActivity,
  TurnId,
} from "@t3tools/contracts";
import { mateInterruptionOf } from "@t3tools/contracts";
import { isCompactCommandMessage } from "@t3tools/shared/userAsk";
import { isToolCallEcho, toolCallEchoKey } from "@t3tools/shared/toolCallEcho";

export type ThreadDetailReducerResult =
  | { readonly kind: "updated"; readonly thread: OrchestrationThread }
  | { readonly kind: "deleted" }
  | { readonly kind: "unchanged" };

const proposedPlanOrder = O.combine<OrchestrationThread["proposedPlans"][number]>(
  O.mapInput(O.String, (p) => p.createdAt),
  O.mapInput(O.String, (p) => p.id),
);

export const activityOrder = O.combineAll<OrchestrationThreadActivity>([
  O.mapInput(O.Number, (a) => a.sequence ?? Number.MAX_SAFE_INTEGER),
  O.mapInput(O.String, (a) => a.createdAt),
  O.mapInput(O.String, (a) => a.id),
]);

// Per-array id index so the streaming append path can reject a re-delivered
// id without rescanning the history. Only arrays this reducer produced are
// indexed: presence also proves the array is activityOrder-sorted, which
// snapshot-loaded arrays (DB order, null sequences first) are not.
const activityIdIndex = new WeakMap<
  ReadonlyArray<OrchestrationThreadActivity>,
  Set<OrchestrationThreadActivity["id"]>
>();

/**
 * Matches the validity rule in `deriveLatestContextWindowSnapshot` (and the
 * server's snapshot-side `dropStaleContextWindowActivities`): rows without a
 * finite, non-negative `usedTokens` are skipped during the consumer's backward
 * walk, so they must not replace an earlier resolvable row here.
 */
function isResolvableContextWindowActivity(activity: OrchestrationThreadActivity): boolean {
  if (activity.kind !== "context-window.updated") {
    return false;
  }
  const payload =
    activity.payload && typeof activity.payload === "object"
      ? (activity.payload as Record<string, unknown>)
      : null;
  const usedTokens = payload?.usedTokens;
  return typeof usedTokens === "number" && Number.isFinite(usedTokens) && usedTokens >= 0;
}

// Per-array echo rows: each call's updates and completions by call, turn and
// instant (`toolCallEchoKey`), so a landing row finds its echo or completion
// without reading the whole history. Moves forward with the fast append path;
// read once from an array that has none.
const echoRowsIndex = new WeakMap<
  ReadonlyArray<OrchestrationThreadActivity>,
  Map<string, ReadonlyArray<OrchestrationThreadActivity>>
>();

function echoRowsOf(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): Map<string, ReadonlyArray<OrchestrationThreadActivity>> {
  const known = echoRowsIndex.get(activities);
  if (known !== undefined) return known;
  const rows = new Map<string, ReadonlyArray<OrchestrationThreadActivity>>();
  for (const activity of activities) {
    const key = toolCallEchoKey(activity);
    if (key !== null) rows.set(key, [...(rows.get(key) ?? []), activity]);
  }
  echoRowsIndex.set(activities, rows);
  return rows;
}

/** Forgets a row a redelivery replaces; true, so it reads as a filter's "drop". */
function forgetEchoRow(
  rows: Map<string, ReadonlyArray<OrchestrationThreadActivity>>,
  row: OrchestrationThreadActivity,
): true {
  const key = toolCallEchoKey(row);
  const known = key === null ? undefined : rows.get(key);
  if (key !== null && known !== undefined) {
    rows.set(
      key,
      known.filter((entry) => entry !== row),
    );
  }
  return true;
}

/** The array without `rows`, each found from the tail, where an echo stands. */
function withoutRows(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  rows: ReadonlyArray<OrchestrationThreadActivity>,
): ReadonlyArray<OrchestrationThreadActivity> {
  const kept = [...activities];
  for (const row of rows) {
    const at = kept.lastIndexOf(row);
    if (at >= 0) kept.splice(at, 1);
  }
  return kept;
}

/**
 * Apply a single orchestration event to an `OrchestrationThread`, returning
 * the updated thread, a deletion signal, or an "unchanged" marker when the
 * event doesn't affect this thread.
 *
 * This is a pure reducer operating on contract types. UI-specific mapping
 * (e.g. resolving attachment preview URLs, normalising model slugs, adding
 * scoped fields like `environmentId`) is the caller's responsibility.
 */
export function applyThreadDetailEvent(
  thread: OrchestrationThread,
  event: OrchestrationEvent,
): ThreadDetailReducerResult {
  switch (event.type) {
    // ── Project events (irrelevant to thread detail) ────────────────
    case "project.created":
    case "project.meta-updated":
    case "project.deleted":
      return { kind: "unchanged" };

    // ── Thread lifecycle ────────────────────────────────────────────
    case "thread.created":
      return {
        kind: "updated",
        thread: {
          id: event.payload.threadId,
          projectId: event.payload.projectId,
          title: event.payload.title,
          modelSelection: event.payload.modelSelection,
          runtimeMode: event.payload.runtimeMode,
          interactionMode: event.payload.interactionMode,
          branch: event.payload.branch,
          worktreePath: event.payload.worktreePath,
          latestTurn: null,
          createdAt: event.payload.createdAt,
          updatedAt: event.payload.updatedAt,
          archivedAt: null,
          settledOverride: null,
          settledAt: null,
          unsettledAt: null,
          activeOrderKey: null,
          autoSettleDisabledAt: null,
          snoozedUntil: null,
          snoozedAt: null,
          deletedAt: null,
          messages: [],
          proposedPlans: [],
          activities: [],
          session: null,
          ...(event.payload.crew !== undefined ? { crew: event.payload.crew } : {}),
        },
      };

    case "thread.deleted":
      return { kind: "deleted" };

    case "thread.archived":
      return {
        kind: "updated",
        thread: {
          ...thread,
          archivedAt: event.payload.archivedAt,
          titleRegeneration: null,
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.unarchived":
      return {
        kind: "updated",
        thread: { ...thread, archivedAt: null, updatedAt: event.payload.updatedAt },
      };

    case "thread.settled":
      return {
        kind: "updated",
        thread: {
          ...thread,
          settledOverride: "settled",
          settledAt: event.payload.settledAt,
          unsettledAt: null,
          activeOrderKey: null,
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.unsettled":
      return {
        kind: "updated",
        thread: {
          ...thread,
          settledOverride: event.payload.reason === "user" ? "active" : null,
          settledAt: null,
          // A thread already pinned active keeps its re-entry stamp: the
          // activity reset that clears the pin must not reorder the list.
          unsettledAt:
            thread.settledOverride === "active"
              ? (thread.unsettledAt ?? null)
              : event.payload.updatedAt,
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.snoozed":
      return {
        kind: "updated",
        thread: {
          ...thread,
          snoozedUntil: event.payload.snoozedUntil,
          snoozedAt: event.payload.snoozedAt,
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.unsnoozed":
      return {
        kind: "updated",
        thread: {
          ...thread,
          snoozedUntil: null,
          snoozedAt: null,
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.pinned":
      return {
        kind: "updated",
        thread: {
          ...thread,
          pinnedAt: event.payload.pinnedAt,
          ...(event.payload.pinOrderKey !== undefined
            ? { pinOrderKey: event.payload.pinOrderKey }
            : {}),
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.unpinned":
      return {
        kind: "updated",
        thread: {
          ...thread,
          pinnedAt: null,
          pinOrderKey: null,
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.pin-reordered":
      return {
        kind: "updated",
        thread: {
          ...thread,
          pinOrderKey: event.payload.orderKey,
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.auto-settle-set":
      return {
        kind: "updated",
        thread: {
          ...thread,
          autoSettleDisabledAt: event.payload.autoSettleDisabledAt,
          updatedAt: event.payload.updatedAt,
        },
      };

    // ── Thread metadata ─────────────────────────────────────────────
    case "thread.meta-updated":
      return {
        kind: "updated",
        thread: {
          ...thread,
          ...(event.payload.title !== undefined ? { title: event.payload.title } : {}),
          ...(event.payload.titleState !== undefined
            ? { titleState: event.payload.titleState }
            : {}),
          ...(event.payload.titleRegeneration !== undefined
            ? { titleRegeneration: event.payload.titleRegeneration }
            : {}),
          ...(event.payload.modelSelection !== undefined
            ? { modelSelection: event.payload.modelSelection }
            : {}),
          ...(event.payload.branch !== undefined ? { branch: event.payload.branch } : {}),
          ...(event.payload.worktreePath !== undefined
            ? { worktreePath: event.payload.worktreePath }
            : {}),
          ...(event.payload.linkedPullRequest !== undefined
            ? { linkedPullRequest: event.payload.linkedPullRequest }
            : {}),
          ...(event.payload.activeOrderKey !== undefined
            ? { activeOrderKey: event.payload.activeOrderKey }
            : {}),
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.runtime-mode-set":
      return {
        kind: "updated",
        thread: {
          ...thread,
          runtimeMode: event.payload.runtimeMode,
          updatedAt: event.payload.updatedAt,
        },
      };

    case "thread.interaction-mode-set":
      return {
        kind: "updated",
        thread: {
          ...thread,
          interactionMode: event.payload.interactionMode,
          updatedAt: event.payload.updatedAt,
        },
      };

    // ── Turn lifecycle ──────────────────────────────────────────────
    case "thread.turn-start-requested": {
      const message = thread.messages.find((each) => each.id === event.payload.messageId);
      const continuesWork =
        event.payload.purpose === "work" ||
        (event.payload.purpose === undefined &&
          message !== undefined &&
          !isCompactCommandMessage(message));
      return {
        kind: "updated",
        thread: {
          ...thread,
          activities: !continuesWork
            ? thread.activities
            : thread.activities.map((activity) => {
                const item = mateInterruptionOf(activity);
                return item?.continuation !== "manual"
                  ? activity
                  : {
                      ...activity,
                      payload: { interruption: { ...item, continuation: "requested" } },
                    };
              }),
          session:
            !continuesWork || thread.session?.interruption == null
              ? thread.session
              : {
                  ...thread.session,
                  interruption: null,
                  status: thread.session.status === "interrupted" ? "ready" : thread.session.status,
                  updatedAt: event.occurredAt,
                },
          ...(event.payload.modelSelection !== undefined
            ? { modelSelection: event.payload.modelSelection }
            : {}),
          runtimeMode: event.payload.runtimeMode,
          interactionMode: event.payload.interactionMode,
          updatedAt: event.occurredAt,
        },
      };
    }
    case "thread.turn-interrupt-requested": {
      if (event.payload.turnId === undefined) {
        return { kind: "unchanged" };
      }
      const latestTurn = thread.latestTurn;
      if (latestTurn === null || latestTurn.turnId !== event.payload.turnId) {
        return { kind: "unchanged" };
      }
      return {
        kind: "updated",
        thread: {
          ...thread,
          latestTurn: {
            ...latestTurn,
            state: "interrupted",
            startedAt: latestTurn.startedAt ?? event.payload.createdAt,
            completedAt: latestTurn.completedAt ?? event.payload.createdAt,
          },
          updatedAt: event.occurredAt,
        },
      };
    }

    // ── Messages ────────────────────────────────────────────────────
    case "thread.message-sent": {
      const message: OrchestrationMessage = {
        id: event.payload.messageId,
        role: event.payload.role,
        text: event.payload.text,
        ...(event.payload.attachments !== undefined
          ? { attachments: event.payload.attachments }
          : {}),
        turnId: event.payload.turnId,
        streaming: event.payload.streaming,
        createdAt: event.payload.createdAt,
        updatedAt: event.payload.updatedAt,
      };

      let found = false;
      const messages = thread.messages.map((entry) => {
        if (entry.id !== message.id) return entry;
        found = true;
        return {
          ...entry,
          text: message.streaming
            ? `${entry.text}${message.text}`
            : message.text.length > 0
              ? message.text
              : entry.text,
          streaming: message.streaming,
          ...(message.turnId !== undefined ? { turnId: message.turnId } : {}),
          ...(message.streaming ? {} : { updatedAt: message.updatedAt }),
          ...(message.attachments !== undefined ? { attachments: message.attachments } : {}),
        };
      });
      if (!found) messages.push(message);
      // Update latestTurn for assistant messages bound to a turn. A completed
      // assistant message only settles the turn once the session is no longer
      // running it — providers may emit several assistant messages per turn
      // (commentary between tool calls), and the turn must stay unsettled
      // until the provider reports turn end. Streaming deltas recompute the
      // same record, so the previous reference is kept when nothing changed.
      const turnStillRunning =
        event.payload.turnId !== null &&
        thread.session?.status === "running" &&
        thread.session.activeTurnId === event.payload.turnId;
      const settlesTurn = !event.payload.streaming && !turnStillRunning;
      const latestTurn = reuseLatestTurn(
        thread.latestTurn,
        event.payload.role === "assistant" &&
          event.payload.turnId !== null &&
          (thread.latestTurn === null || thread.latestTurn.turnId === event.payload.turnId)
          ? {
              turnId: event.payload.turnId,
              state: settlesTurn
                ? thread.latestTurn?.state === "interrupted"
                  ? "interrupted"
                  : thread.latestTurn?.state === "error"
                    ? "error"
                    : "completed"
                : "running",
              requestedAt:
                thread.latestTurn?.turnId === event.payload.turnId
                  ? thread.latestTurn.requestedAt
                  : event.payload.createdAt,
              startedAt:
                thread.latestTurn?.turnId === event.payload.turnId
                  ? (thread.latestTurn.startedAt ?? event.payload.createdAt)
                  : event.payload.createdAt,
              completedAt: settlesTurn
                ? event.payload.updatedAt
                : thread.latestTurn?.turnId === event.payload.turnId
                  ? (thread.latestTurn.completedAt ?? null)
                  : null,
              assistantMessageId: event.payload.messageId,
            }
          : thread.latestTurn,
      );

      return {
        kind: "updated",
        thread: {
          ...thread,
          messages,
          latestTurn,
          updatedAt: event.occurredAt,
        },
      };
    }

    // ── Session ─────────────────────────────────────────────────────
    case "thread.session-set": {
      // Leaving the "running" session status is the turn-end signal: settle a
      // still-running latest turn so its duration reflects the whole turn.
      const settledTurnState = settledTurnStateForSessionStatus(event.payload.session.status);
      const latestTurn = reuseLatestTurn(
        thread.latestTurn,
        event.payload.session.status === "running" && event.payload.session.activeTurnId !== null
          ? {
              turnId: event.payload.session.activeTurnId,
              state: "running",
              requestedAt:
                thread.latestTurn?.turnId === event.payload.session.activeTurnId
                  ? thread.latestTurn.requestedAt
                  : event.payload.session.updatedAt,
              startedAt:
                thread.latestTurn?.turnId === event.payload.session.activeTurnId
                  ? (thread.latestTurn.startedAt ?? event.payload.session.updatedAt)
                  : event.payload.session.updatedAt,
              completedAt: null,
              assistantMessageId:
                thread.latestTurn?.turnId === event.payload.session.activeTurnId
                  ? thread.latestTurn.assistantMessageId
                  : null,
            }
          : thread.latestTurn !== null &&
              (thread.latestTurn.state === "running" ||
                (thread.latestTurn.state === "error" &&
                  thread.latestTurn.turnId === event.payload.session.interruption?.turnId)) &&
              settledTurnState !== null
            ? {
                ...thread.latestTurn,
                state: settledTurnState,
                // The session leaving "running" is the authoritative turn end.
                completedAt: event.payload.session.updatedAt,
              }
            : thread.latestTurn,
      );

      return {
        kind: "updated",
        thread: {
          ...thread,
          session: {
            ...event.payload.session,
            interruption:
              event.payload.session.interruption === undefined
                ? (thread.session?.interruption ?? null)
                : event.payload.session.interruption,
          },
          activities:
            event.payload.session.interruption == null
              ? thread.activities
              : [
                  ...thread.activities,
                  {
                    id: event.eventId,
                    tone: "info",
                    kind: "runtime.interrupted",
                    summary: "Interrupted by a Mate restart",
                    payload: { interruption: event.payload.session.interruption },
                    turnId: event.payload.session.interruption.turnId,
                    sequence: event.sequence,
                    createdAt: event.payload.session.updatedAt,
                  },
                ],
          latestTurn,
          updatedAt: event.occurredAt,
        },
      };
    }

    case "thread.session-stop-requested":
      return thread.session === null
        ? { kind: "unchanged" }
        : {
            kind: "updated",
            thread: {
              ...thread,
              session: {
                ...thread.session,
                status: "stopped",
                activeTurnId: null,
                updatedAt: event.payload.createdAt,
              },
              updatedAt: event.occurredAt,
            },
          };

    // ── Proposed plans ──────────────────────────────────────────────
    case "thread.proposed-plan-upserted": {
      const proposedPlan = event.payload.proposedPlan;

      const proposedPlans = pipe(
        thread.proposedPlans,
        Arr.filter((entry) => entry.id !== proposedPlan.id),
        Arr.append(proposedPlan),
        Arr.sort(proposedPlanOrder),
      );

      return {
        kind: "updated",
        thread: { ...thread, proposedPlans, updatedAt: event.occurredAt },
      };
    }

    // ── Activities ──────────────────────────────────────────────────
    case "thread.activity-appended": {
      const activity = event.payload.activity;
      // A call's echo — the update sent with its completion, the same instant
      // and payload (`isToolCallEcho`) — is dropped wherever it sorts: landing
      // after its completion it changes nothing, and a completion landing
      // after it takes it out. A reload's snapshot drops it the same way.
      const echoKey = toolCallEchoKey(activity);
      const peers = echoKey === null ? undefined : echoRowsOf(thread.activities).get(echoKey);
      if (
        activity.kind === "tool.updated" &&
        peers?.some((peer) => isToolCallEcho(activity, peer)) === true
      ) {
        return { kind: "unchanged" };
      }
      const echoes =
        activity.kind === "tool.completed" && peers !== undefined
          ? peers.filter((peer) => isToolCallEcho(peer, activity))
          : [];
      // A resolvable context-window update supersedes earlier resolvable ones
      // for the same turn: consumers only read the latest value (walking the
      // array backwards), and providers stream these updates continuously, so
      // retaining the history grows the thread by thousands of rows over a
      // long session. Mirrors the server-side snapshot rule in
      // dropStaleContextWindowActivities; retention stays per turn.
      const supersedesContextWindow = isResolvableContextWindowActivity(activity);
      // Live streams append in order: an unseen id sorting at/after the tail
      // of a known-sorted array appends without re-filtering and re-sorting
      // the whole history on every event. A completion's echoes come off the
      // tail first: an echo of the same instant may sort just above it (the
      // ids are random), and the completion then appends where it stood. The
      // id set and the echo rows move forward to the new array; a superseded
      // array falls back to the sorting path, and its echo rows are read anew
      // when next asked for. A context-window update streaming in takes the
      // same path: the rows it supersedes come out in one pass, and what is
      // left stays in order.
      const ids = activityIdIndex.get(thread.activities);
      const base = echoes.length === 0 ? thread.activities : withoutRows(thread.activities, echoes);
      const lastActivity = base.at(-1);
      if (
        ids !== undefined &&
        (lastActivity === undefined || activityOrder(lastActivity, activity) <= 0) &&
        !ids.has(activity.id)
      ) {
        const rows = echoRowsOf(thread.activities);
        const superseded: OrchestrationThreadActivity[] = [];
        const kept = supersedesContextWindow
          ? base.filter((entry) => {
              const replaced =
                entry.turnId === activity.turnId && isResolvableContextWindowActivity(entry);
              if (replaced) superseded.push(entry);
              return !replaced;
            })
          : base;
        const activities = Arr.append(kept, activity);
        activityIdIndex.delete(thread.activities);
        echoRowsIndex.delete(thread.activities);
        ids.add(activity.id);
        for (const echo of echoes) ids.delete(echo.id);
        for (const row of superseded) {
          ids.delete(row.id);
          forgetEchoRow(rows, row);
        }
        if (echoKey !== null) {
          rows.set(echoKey, [...(peers ?? []).filter((row) => !echoes.includes(row)), activity]);
        }
        activityIdIndex.set(activities, ids);
        echoRowsIndex.set(activities, rows);
        return {
          kind: "updated",
          thread: {
            ...thread,
            activities,
            updatedAt: event.occurredAt,
          },
        };
      }
      const rows = echoRowsOf(thread.activities);
      echoRowsIndex.delete(thread.activities);
      const activities = pipe(
        thread.activities,
        Arr.filter(
          (entry) =>
            !(entry.id === activity.id && forgetEchoRow(rows, entry)) &&
            !echoes.includes(entry) &&
            !(
              supersedesContextWindow &&
              entry.turnId === activity.turnId &&
              isResolvableContextWindowActivity(entry)
            ),
        ),
        Arr.append(activity),
        Arr.sort(activityOrder),
      );
      activityIdIndex.set(activities, new Set(activities.map((entry) => entry.id)));
      if (echoKey !== null) {
        rows.set(echoKey, [
          ...(rows.get(echoKey) ?? []).filter((row) => !echoes.includes(row)),
          activity,
        ]);
      }
      echoRowsIndex.set(activities, rows);

      return {
        kind: "updated",
        thread: { ...thread, activities, updatedAt: event.occurredAt },
      };
    }

    // ── Events that don't mutate thread state directly ──────────────
    case "thread.approval-response-requested":
    case "thread.user-input-response-requested":
      return { kind: "unchanged" };
  }

  // Forward-compatible: ignore unrecognized event types.
  return { kind: "unchanged" };
}

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * Turn state to settle a still-running latest turn with when its session
 * leaves the "running" status, or null while the session is (re)starting or
 * running and the turn must stay unsettled.
 */
function settledTurnStateForSessionStatus(
  status: OrchestrationSession["status"],
): "completed" | "interrupted" | "error" | null {
  switch (status) {
    case "idle":
    case "ready":
      return "completed";
    case "error":
      return "error";
    case "interrupted":
    case "stopped":
      return "interrupted";
    case "starting":
    case "running":
      return null;
  }
}

/**
 * Returns `previous` when `next` matches it field for field, otherwise `next`.
 * Streaming cases recompute the latest turn on every delta, and keeping the
 * old reference lets selectors and memos keyed on `latestTurn` skip work.
 */
function reuseLatestTurn(
  previous: OrchestrationLatestTurn | null,
  next: OrchestrationLatestTurn | null,
): OrchestrationLatestTurn | null {
  if (previous === null || next === null) {
    return next;
  }
  return previous.turnId === next.turnId &&
    previous.state === next.state &&
    previous.requestedAt === next.requestedAt &&
    previous.startedAt === next.startedAt &&
    previous.completedAt === next.completedAt &&
    previous.assistantMessageId === next.assistantMessageId &&
    previous.sourceProposedPlan?.threadId === next.sourceProposedPlan?.threadId &&
    previous.sourceProposedPlan?.planId === next.sourceProposedPlan?.planId
    ? previous
    : next;
}
